import { useState } from 'react';
import { Select } from '../../components/ui';
import { originLabel, originsFor } from './leadFields';
import type { LeadSource } from './leadFields';
import { SOURCE_LABELS, leadName } from './types';
import type { Lead } from './types';

/**
 * Lead Source and Lead Origin, in one table cell, with the origin editable
 * from the row.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Measured 28 Aug on production: 674 of 693 leads carry NO Lead Origin, and
 * the entire advertising vocabulary — LinkedIn Ads, Facebook Ads, Instagram,
 * Facebook, TikTok — had never been used once. Not because the field is
 * broken. Because setting it costs opening a drawer, and nobody opens a
 * drawer 674 times.
 *
 * That empty column is the whole reason Alex's standing question — "how many
 * leads did the ads bring in, and what happened to them" — has been
 * unanswerable for two weeks. No filter, saved view or report can find a fact
 * nobody recorded. So the fix is not more filtering. It is making the tagging
 * cheap enough to actually happen.
 *
 * ── Why it lives inside the Lead Source column ─────────────────────────────
 *
 * Kyu asked for a fourteenth column. The mockup offered that and this, and he
 * picked this. Lead Source and Lead Origin are the same question at two
 * depths — where did this person come from, coarse then fine — so the finer
 * answer belongs under the coarser one rather than beside it. It also costs
 * no width, which at his window means no column falls off the right edge, and
 * it keeps the thirteen-column count he set himself.
 *
 * The table already stacks a second line in a cell in three places: Status
 * over Nurture, Last touch over "N touches", Contacted over BAD CONTACT. This
 * is that object, not a new one.
 *
 * ── Lead Source is NOT editable here, and that is deliberate ───────────────
 *
 * Kyu asked for it and I said no. It is the consent record: changing it
 * asserts whether somebody agreed to be emailed. The drawer's "Correct this"
 * requires a typed reason, writes an entry to the lead's history with the
 * actor's name, and is admin-only. A cell that saves on selection throws away
 * all three.
 *
 * It costs him nothing in practice — 689 of 693 leads already have a Lead
 * Source, because the team finished that queue. The empty field is the origin.
 */
export function RowOriginCell({
  lead,
  sources,
  editing,
  onOpen,
  onClose,
  onSave,
}: {
  lead: Lead;
  sources: LeadSource[];
  /** Only one cell in the table is open at a time; the page owns that. */
  editing: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** '' clears the origin. Resolves when the write lands. */
  onSave: (originId: string) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const current = sources.find((row) => row.id === lead.lead_source_id) ?? null;

  return (
    <>
      {/* The consent record. Plain ink because it is on every row, and the one
          value that earns colour is the one saying the answer is missing. */}
      {lead.source === null ? (
        <span className="lead-plain lead-plain--missing">Not set</span>
      ) : (
        <span className="lead-plain">{SOURCE_LABELS[lead.source]}</span>
      )}

      {editing ? (
        <Select
          label="Lead Origin"
          labelHidden
          className="ui-input ui-select row-origin-select"
          autoFocus
          disabled={saving}
          value={lead.lead_source_id ?? ''}
          onBlur={onClose}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              onClose();
            }
          }}
          onChange={(event) => {
            const next = event.target.value;
            // Picking what is already there is not a write. Without this,
            // opening the cell and closing it with the mouse still round-trips
            // to the database and stamps updated_by.
            if (next === (lead.lead_source_id ?? '')) {
              onClose();
              return;
            }
            setSaving(true);
            void onSave(next).finally(() => {
              setSaving(false);
              onClose();
            });
          }}
          options={[
            // A real answer, not a placeholder: somebody removing an origin
            // they set by mistake has to be able to say so. Same reasoning as
            // the drawer's field, which this deliberately mirrors.
            { value: '', label: 'Not said' },
            ...originsFor(sources, lead.source, lead.lead_source_id).map((row) => ({
              value: row.id,
              label: originLabel(row),
            })),
          ]}
        />
      ) : (
        <span className="lead-sub">
          <button
            type="button"
            className={current === null ? 'row-origin row-origin--empty' : 'row-origin'}
            /* The label is clipped to one line so the row cannot grow (see
               .row-origin). A long origin is still readable on hover, and the
               aria-label below already carried it for a screen reader. */
            title={current === null ? undefined : originLabel(current)}
            // The accessible name carries the lead, because "Set origin"
            // repeated 693 times tells a screen-reader user nothing about
            // which row they are on.
            aria-label={
              current === null
                ? `Set Lead Origin for ${leadName(lead)}`
                : `Lead Origin for ${leadName(lead)}: ${originLabel(current)}. Change it.`
            }
            onClick={onOpen}
          >
            {current === null ? 'Set origin' : originLabel(current)}
          </button>
        </span>
      )}
    </>
  );
}
